// shared-lib/cabinet-runs/profiles.js
//
// Cabinet PROFILES (bathroom-cabinet-runs plan, 2026-09-01). A profile
// belongs to the RUN — the same way `family` does — and is a SPEC the fill
// consumes: body depth/height, countertop stack, uppers default, labels.
// It is stamped at draw time from the room's kind and stored forever; a
// re-solve never re-guesses it from the room.
//
// The profile says how big the pieces are. WHAT pieces exist — which fronts a
// bay may wear, which appliance bays a run may be given — is `catalog.js`,
// where each entry declares the profiles that offer it. The two are split so
// that adding a kitchen-only front is one catalog entry rather than an edit to
// every profile, and so a consumer asking "may I offer this?" has exactly one
// place to ask. Everything a SURFACE needs to know about a run's kind is
// reachable from its stored profile id; nothing should re-read the room.
//
// Every number is RE-EXPORTED from the constants file its subsystem already
// owns (kitchen-assembly / vanity-assembly), never restated, so the profile
// cannot drift from the builders that consume those constants directly.
//
// `profileOf` resolves an unset or unknown profile to `kitchen` — the only
// behaviour that existed before profiles did, so every stored run keeps
// solving exactly as it always has.

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  KITCHEN_COUNTERTOP_FRONT_OVERHANG,
} = require('../kitchen-assembly/constants');
const { isAsBuilt } = require('./sizing');
const {
  VANITY_DIMENSIONS,
  VANITY_SINK_BASE_WIDTH,
  SHALLOW_VANITY_DEPTH,
} = require('../vanity-assembly/constants');

const { primaryRoomKind } = require('../rooms/roomKind');

const KITCHEN_PROFILE_ID = 'kitchen';
const VANITY_PROFILE_ID = 'vanity';

const CABINET_PROFILES = {
  [KITCHEN_PROFILE_ID]: {
    id: KITCHEN_PROFILE_ID,
    depth: KITCHEN_CABINET_DIMENSIONS.depth, // 24"
    height: KITCHEN_CABINET_DIMENSIONS.height, // 34.5"
    counterThickness: KITCHEN_COUNTERTOP_THICKNESS, // 1.5"
    counterOverhang: KITCHEN_COUNTERTOP_FRONT_OVERHANG, // 1"
    uppersDefault: true,
    uppersAllowed: true,
    lowerLabel: 'Kitchen Cabinet',
    counterLabel: 'Kitchen Countertop',
    // The Add Elements tray's heading for the run tools. Not cosmetic: the
    // category holds the run plus the appliance bays this profile offers
    // (`catalog.js`), so calling it "Kitchen" over a vanity's Run + Sink
    // would misname what is in it.
    trayLabel: 'Kitchen',
    // The body depths this profile OFFERS. `depth` above is the default and
    // must be one of them.
    //
    // ONE for the kitchen, deliberately (user ruling 2026-09-21: "we don't
    // have to change kitchen at the moment"). The scans agree it would buy
    // little — a kitchen base is 24" almost always, median, p75 AND p90 all
    // 24.0 across 327 corpus bodies — and offering a SHALLOW one is not free:
    // the kitchen basin is a fixed 27"x17" that a 13" counter cannot hold, so
    // a shallow kitchen run with a sink needs a rule about what bowl belongs
    // there before the option is honest. A single entry means the picker
    // hides itself on a kitchen run, so this branch changes nothing a kitchen
    // user sees. `SHALLOW_CABINET_DEPTH` (12") is the value to add back.
    depths: [
      {
        id: 'standard',
        name: 'Standard',
        depth: KITCHEN_CABINET_DIMENSIONS.depth,
      },
    ],
    // No override: `FOOTPRINT_BY_TYPE` (bays.js) IS the kitchen's table, so
    // the kitchen path reads the same numbers it always has and cannot drift
    // by way of this file.
    applianceFootprints: {},
  },
  [VANITY_PROFILE_ID]: {
    id: VANITY_PROFILE_ID,
    depth: VANITY_DIMENSIONS.singleSink.depth, // 21"
    height: VANITY_DIMENSIONS.singleSink.height, // 32"
    counterThickness: VANITY_DIMENSIONS.countertop.thickness, // 1.5"
    counterOverhang: VANITY_DIMENSIONS.countertop.overhang, // 1"
    // A bathroom wants a mirror over the counter, not cabinet boxes.
    uppersDefault: false,
    // A VANITY HAS NO UPPERS TO OFFER (user directive 2026-09-28). The run's
    // upper tier is kitchen wall cabinetry — 30" boxes on a 54" band over a
    // 36" counter — and what belongs over a bathroom basin is the mirror and
    // its light, which the vanity's own accessory pass owns
    // (`vanityRunAccessories.js`). Unlike `uppersDefault` this is not a
    // default: a stored `uppers: true` does not override it, so the panel and
    // the fill cannot disagree.
    uppersAllowed: false,
    lowerLabel: 'Vanity Cabinet',
    counterLabel: 'Vanity Countertop',
    trayLabel: 'Cabinets',
    // Scanned vanities cluster tighter and shallower than kitchen bases
    // (median 21.6", p25 19.0", p75 22.7" over 42 corpus bodies), so the
    // standard is 21" and the second choice is the narrow carcass
    // `SHALLOW_VANITY_DEPTH` already names — the floor below which a catalog
    // basin will not sit in a cabinet at all.
    depths: [
      {
        id: 'standard',
        name: 'Standard',
        depth: VANITY_DIMENSIONS.singleSink.depth,
      },
      { id: 'narrow', name: 'Narrow', depth: SHALLOW_VANITY_DEPTH },
    ],
    // What an appliance BAY reserves, overriding `FOOTPRINT_BY_TYPE` for the
    // kinds this profile offers. Only the sink differs, and all three numbers
    // do: a bathroom sink base is 30" rather than 36"
    // (`VANITY_SINK_BASE_WIDTH` says why), and it must reserve the body this
    // profile actually BUILDS — the bay used to claim a kitchen 24"×34.5"
    // footprint under a 21"×32" cabinet.
    applianceFootprints: {
      sink: {
        width: VANITY_SINK_BASE_WIDTH,
        depth: VANITY_DIMENSIONS.singleSink.depth,
        height: VANITY_DIMENSIONS.singleSink.height,
      },
    },
  },
};

/**
 * The body depths a profile offers, in picker order. Unknown/unset → kitchen,
 * the same resolution `profileOf` uses.
 */
function depthOptionsFor(profileId) {
  return (profileById(profileId) || CABINET_PROFILES[KITCHEN_PROFILE_ID])
    .depths;
}

/**
 * The depth this run's bodies are built at, as a SPEC — before any measured
 * or site override.
 *
 * A stored `run.depth` is the user's choice and wins; otherwise the profile's
 * default. It is NOT snapped to the offered list: a run derived from a scan
 * measured at 21.6" carries that number, and rounding it to the nearest chip
 * would rebuild a room's cabinetry to make a picker tidy. The picker's job is
 * to SHOW an off-list depth (the same rule the width chips already follow),
 * not to erase it.
 *
 * Where this sits against the other answers is `materializeRun`'s
 * `buildFillCtx`: an explicit `ctx.cabinetDepth` from the call site wins over
 * everything (the minimal-change kitchen re-solving a scan at the standard),
 * then this, then the scan's measured median, then the profile default.
 */
function runDepthSpec(run) {
  const stored = run?.depth;
  if (typeof stored === 'number' && stored > 0) return stored;
  return profileOf(run).depth;
}

/**
 * How far this run's finished countertop sits ABOVE THE FLOOR — the profile's
 * body height plus its counter thickness.
 *
 * The one conversion, because there were three and two of them said 36".
 * `upperBand.js` already declared itself "the one place" this happens, and
 * `openings.js` and `runEndClosure.js` each carried their own copy built from
 * the KITCHEN constants — so on a vanity run the sill test that decides
 * whether a window blocks a base cabinet measured against a counter 2.5"
 * higher than the one being built. A bathroom window sill between 33.5" and
 * 36" therefore reserved its own width out of a vanity that clears it.
 *
 * Takes the RUN, like every other profile reader, so an unset profile gives
 * the kitchen answer and nothing stored changes meaning.
 */
function counterTopOffsetFor(run) {
  const profile = profileOf(run);
  return runBodyHeight(run) + profile.counterThickness;
}

function profileById(id) {
  return CABINET_PROFILES[id] || null;
}

/** The dimension profile a run solves with. Unset/unknown → kitchen. */
function profileOf(run) {
  return profileById(run?.profile) || CABINET_PROFILES[KITCHEN_PROFILE_ID];
}

/**
 * The body HEIGHT this run is built at.
 *
 * The profile's own, unless the run is being kept AS BUILT and recorded a
 * measured height — then it is the height the room actually contains.
 *
 * Why it is not simply the profile's (fixed 2026-09-28): as-built honoured the
 * measured DEPTH and silently standardised the height, so converting a scanned
 * 30 ¼" vanity rebuilt it at the profile's 32" and lifted the countertop 1 ¾"
 * — while the basin the scan measured stayed where it was, leaving its rim
 * that far BELOW the underside of the slab. An undermount hanging in mid-air
 * is what a user sees; "as built" claiming two of three dimensions is the
 * cause.
 *
 * Only a VANITY run records a measurement (nothing else writes `measured`),
 * and a kitchen deliberately re-solves a scanned base at the standard 34.5"
 * (`minimal-change.md`), so this reads as the profile's height everywhere
 * else.
 */
function runBodyHeight(run) {
  const profile = profileOf(run);
  const measured = run?.measured?.height;
  if (isAsBuilt(run) && measured > 0) return measured;
  // A vendor family builds at its own body height for this profile (Venus
  // vanities are 34.5", where the vanity profile is 32").
  const familyIn = familyBodyHeightIn(run, profile.id);
  return familyIn ? familyIn * 0.0254 : profile.height;
}

/**
 * The run's family's body height for a profile, in inches, or null.
 * `families.js` is required lazily: it loads the corner-blind sets and the
 * module solver, and this module is read by `upperBand.js` on the way in.
 */
function familyBodyHeightIn(run, profileId) {
  if (!run?.family) return null;
  const { familyById } = require('./families');
  const inches = familyById(run.family)?.spec?.bodyHeights?.[profileId];
  return inches > 0 ? inches : null;
}

// How close a profile depth must come to a line's to be the line's: half an
// inch, the resolution the Venus price list is read at (`resolveSku.inches`)
// — the vanity profile's 0.533 m is 20.98", Venus's vanity 21".
const LINE_DEPTH_TOL_IN = 0.5;

/**
 * The depth, in inches, the run's cabinet LINE builds a vanity at, or null
 * where the line does not decide it — no line, a line with no size catalog,
 * or a kitchen run (its profile offers one depth already).
 *
 * Read from the line's own price list (`vendorSizes.vanityBodyOf`: Venus's
 * vanity units are all 21" deep), never written down a second time. Venus
 * stays at 21" (user ruling 2026-11-02): its kitchen way of building shallow
 * — 12" wall cabinets on a kick — is too shallow for a vanity, so the narrow
 * 15.3" carcass the vanity profile offers is not one Venus makes, and a
 * narrow Venus vanity was drawn 15.3" deep and priced as the 21" parts.
 *
 * `families.js` and `vendorSizes.js` are required lazily, as
 * `familyBodyHeightIn` requires the first: this module is read on the way in.
 */
function lineDepthIn(run) {
  if (!run?.family || profileOf(run).id !== VANITY_PROFILE_ID) return null;
  const { familyById } = require('./families');
  const { vanityBodyOf } = require('./vendorSizes');
  const body = vanityBodyOf(familyById(run.family));
  return body?.depthIn > 0 ? body.depthIn : null;
}

/** Whether `depth` (metres) is one the run's line builds — any, with no line. */
function depthInLine(run, depth) {
  const lineIn = lineDepthIn(run);
  if (lineIn == null) return true;
  return Math.abs(depth / 0.0254 - lineIn) <= LINE_DEPTH_TOL_IN;
}

/**
 * The body depths THIS run may be built at, in picker order: its profile's,
 * narrowed to what its cabinet line makes (`lineDepthIn`). A Venus vanity
 * keeps Standard alone, so the panel's depth row — which hides below two
 * options — does not show; a generic one keeps Standard and Narrow.
 */
function depthOptionsForRun(run) {
  const offered = depthOptionsFor(profileOf(run).id);
  const lineIn = lineDepthIn(run);
  if (lineIn == null) return offered;
  const inLine = offered.filter((d) => depthInLine(run, d.depth));
  return inLine.length
    ? inLine
    : [{ id: 'standard', name: 'Standard', depth: lineIn * 0.0254 }];
}

/**
 * Whether the upper-cabinet pass runs for this run. A profile that does not
 * ALLOW uppers ends it there (a vanity: see `uppersAllowed`); otherwise an
 * explicit stored `uppers` boolean wins, and only an unset flag falls to the
 * profile's default. Replaces the bare `run.uppers !== false` gate, which
 * encoded default-ON for every run.
 */
function uppersEnabled(run) {
  const profile = profileOf(run);
  // The profile's refusal is absolute; only where uppers are ON OFFER does the
  // stored flag get a say. A vanity that somehow carries `uppers: true` — an
  // older run toggled before the row was withdrawn — builds none.
  if (profile.uppersAllowed === false) return false;
  if (run?.uppers != null) return run.uppers !== false;
  return profile.uppersDefault;
}

/**
 * The profile a NEW run drawn in this room should carry — decided once, at
 * draw commit, and stored on the run (a re-solve never re-guesses from the
 * room). A bathroom draws vanity cabinetry; everything else keeps the
 * kitchen default. Following the `family` convention, the kitchen default is
 * expressed as an UNSET profile, so kitchen runs' stored shape is unchanged.
 */
function profileIdForRoom(room) {
  return primaryRoomKind(room) === 'bathroom'
    ? VANITY_PROFILE_ID
    : KITCHEN_PROFILE_ID;
}

module.exports = {
  CABINET_PROFILES,
  KITCHEN_PROFILE_ID,
  VANITY_PROFILE_ID,
  profileById,
  profileOf,
  profileIdForRoom,
  uppersEnabled,
  runBodyHeight,
  depthOptionsFor,
  depthOptionsForRun,
  depthInLine,
  lineDepthIn,
  runDepthSpec,
  counterTopOffsetFor,
};
